import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, Check } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    building: '',
    budget: '',
    timeline: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Thank you for your enquiry. We\'ll respond within 1-2 business days.');
  };

  const budgetRanges = [
    'Under £25,000',
    '£25,000 - £50,000',
    '£50,000 - £100,000',
    '£100,000+',
    'Prefer not to say'
  ];

  const timelineOptions = [
    'Immediate (within 2 weeks)',
    'Near-term (1-2 months)',
    'Planning stage (3+ months)',
    'Exploratory conversation'
  ];

  return (
    <main data-testid="contact-page" className="pt-36 md:pt-40 min-h-screen">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#111111',
            color: '#F7F2E8',
            border: 'none',
            borderRadius: '0'
          }
        }} 
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-80px)]">
        {/* Left Column - Dark */}
        <section 
          data-testid="contact-info"
          className="bg-charcoal text-ivory py-16 md:py-20 px-6 md:px-12 lg:px-16 flex flex-col justify-center"
        >
          <motion.div 
            className="max-w-md"
            initial="hidden"
            animate="visible"
            variants={fadeUpVariant}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              Get in touch
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-ivory tracking-tight leading-[1.1] mb-8">
              Let's Start a Conversation
            </h1>
            <p className="text-lg text-ivory/70 font-light leading-relaxed mb-12">
              Every partnership begins with understanding. Share your objectives and we'll outline how we can help.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-12">
              <a
                href="mailto:novumroyale@hotmail.com"
                data-testid="contact-email"
                className="flex items-center space-x-4 text-ivory/80 hover:text-gold transition-colors duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-ivory/20 group-hover:border-gold transition-colors duration-300">
                  <Mail size={20} className="text-gold" strokeWidth={1.5} />
                </div>
                <div>
                  <span className="text-xs uppercase tracking-[0.15em] text-ivory/40 block mb-1">Email</span>
                  <span className="text-base">novumroyale@hotmail.com</span>
                </div>
              </a>
            </div>

            {/* Response Time */}
            <div className="pt-8 border-t border-ivory/10">
              <p className="text-sm text-ivory/50 font-light leading-relaxed">
                We respond to all enquiries within 1-2 business days. For urgent matters, please indicate in your message.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Right Column - Light with Form */}
        <section 
          data-testid="contact-form-section"
          className="bg-ivory py-16 md:py-20 px-6 md:px-12 lg:px-16 flex flex-col justify-center"
        >
          <motion.div 
            className="max-w-lg w-full mx-auto"
            initial="hidden"
            animate="visible"
            variants={fadeUpVariant}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {isSubmitted ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-8 flex items-center justify-center border border-gold bg-gold/10">
                  <Check size={32} className="text-gold" strokeWidth={1.5} />
                </div>
                <h2 className="font-heading text-3xl text-charcoal font-medium mb-4">
                  Received
                </h2>
                <p className="text-base text-softcharcoal/70 font-light leading-relaxed">
                  Thank you for your enquiry. We'll respond within 1-2 business days.
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-2xl md:text-3xl text-charcoal font-medium mb-8">
                  Request an Introduction
                </h2>
                
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                  {/* Name */}
                  <div>
                    <label 
                      htmlFor="name" 
                      className="text-xs uppercase tracking-[0.15em] text-softcharcoal/60 font-semibold mb-3 block"
                    >
                      Name <span className="text-gold">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="input-name"
                      value={formState.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-softcharcoal/20 px-0 py-4 text-charcoal placeholder:text-softcharcoal/30 focus:border-gold focus:outline-none transition-colors"
                      placeholder="Your full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label 
                      htmlFor="email" 
                      className="text-xs uppercase tracking-[0.15em] text-softcharcoal/60 font-semibold mb-3 block"
                    >
                      Email <span className="text-gold">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="input-email"
                      value={formState.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-softcharcoal/20 px-0 py-4 text-charcoal placeholder:text-softcharcoal/30 focus:border-gold focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label 
                      htmlFor="company" 
                      className="text-xs uppercase tracking-[0.15em] text-softcharcoal/60 font-semibold mb-3 block"
                    >
                      Company <span className="text-gold">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      data-testid="input-company"
                      value={formState.company}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-softcharcoal/20 px-0 py-4 text-charcoal placeholder:text-softcharcoal/30 focus:border-gold focus:outline-none transition-colors"
                      placeholder="Company name"
                    />
                  </div>

                  {/* What are you building */}
                  <div>
                    <label 
                      htmlFor="building" 
                      className="text-xs uppercase tracking-[0.15em] text-softcharcoal/60 font-semibold mb-3 block"
                    >
                      What are you building? <span className="text-gold">*</span>
                    </label>
                    <textarea
                      id="building"
                      name="building"
                      data-testid="input-building"
                      value={formState.building}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full bg-transparent border-b border-softcharcoal/20 px-0 py-4 text-charcoal placeholder:text-softcharcoal/30 focus:border-gold focus:outline-none transition-colors resize-none"
                      placeholder="Brief description of your project or objectives"
                    />
                  </div>

                  {/* Budget Range (Optional) */}
                  <div>
                    <label 
                      htmlFor="budget" 
                      className="text-xs uppercase tracking-[0.15em] text-softcharcoal/60 font-semibold mb-3 block"
                    >
                      Budget Range <span className="text-softcharcoal/40">(Optional)</span>
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      data-testid="input-budget"
                      value={formState.budget}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-softcharcoal/20 px-0 py-4 text-charcoal focus:border-gold focus:outline-none transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="text-softcharcoal/30">Select range</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range} className="text-charcoal bg-ivory">
                          {range}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Timeline */}
                  <div>
                    <label 
                      htmlFor="timeline" 
                      className="text-xs uppercase tracking-[0.15em] text-softcharcoal/60 font-semibold mb-3 block"
                    >
                      Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      data-testid="input-timeline"
                      value={formState.timeline}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-softcharcoal/20 px-0 py-4 text-charcoal focus:border-gold focus:outline-none transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="text-softcharcoal/30">Select timeline</option>
                      {timelineOptions.map((option) => (
                        <option key={option} value={option} className="text-charcoal bg-ivory">
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    data-testid="submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-charcoal text-ivory px-8 py-5 text-sm uppercase tracking-widest font-medium hover:bg-softcharcoal transition-colors duration-300 flex items-center justify-center space-x-3 disabled:opacity-70 disabled:cursor-not-allowed mt-10"
                  >
                    {isSubmitting ? (
                      <>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Enquiry</span>
                        <Send size={16} strokeWidth={1.5} />
                      </>
                    )}
                  </button>
                </form>

                {/* Privacy Note */}
                <p className="mt-8 text-xs text-softcharcoal/50 font-light leading-relaxed">
                  By submitting this form, you agree to our privacy practices. Your information is handled with complete discretion and will never be shared with third parties.
                </p>
              </>
            )}
          </motion.div>
        </section>
      </div>
    </main>
  );
};

export default Contact;
